import type { DcpCheckStatus, SageBankAccountRow, SageCheckRow } from '@/lib/db-tenant'
import type { RenderedSageSyncWarning } from '@dcp/shared/schemas/integrations'

/**
 * Sage transaction-state codes that feed the Outstanding Checks view.
 *
 * Only Posted (`'C'`) checks are considered outstanding (DCP-244). DCP maps
 * Posted checks to status `Issued`. Voided is a user-only override managed via
 * the DCP inline editor. Paid comes either from the user (override or a Date
 * Cashed) or, since DCP-320, from Sage itself: an un-overridden check that Sage
 * reports reconciled (`sage_cleared = 'T'`) derives as Paid — see
 * `effectiveCheckStatus`.
 *
 * The OutstandingChecksPage list filter and `aggregateChecksByEntity` below
 * MUST stay in lockstep with this constant — it is the single source of truth
 * that keeps the SageIntacctPage badge counter in agreement with the rows
 * shown on /banking/outstanding-checks. The SQL function
 * `sage_checks_aggregate_by_entity` must also match (see its WHERE clause).
 */
export const OUTSTANDING_CHECK_STATES = ['C'] as const
export type OutstandingCheckState = (typeof OUTSTANDING_CHECK_STATES)[number]

/**
 * Effective display/calculation status of a check (DCP-213, extended by DCP-320).
 *
 * Order of authority, highest first:
 *   1. `dcp_check_status` — an explicit human override always wins. A user who
 *      says a check is still Issued outranks every derived signal, including
 *      Sage's reconciliation flag.
 *   2. `date_cashed` — a DCP-recorded cash date means the check is 'paid'.
 *   3. `sage_cleared === 'T'` — DCP-320: Sage has reconciled the check against
 *      a bank statement. "Cleared" means the statement CONTAINED the check, so
 *      the bank has already paid it; the check is therefore 'paid' even though
 *      nobody typed a Date Cashed. Only the exact DAY is unknown (the statement
 *      carries a reconciliation period, not a cash date), which is why
 *      `date_cashed` stays empty and this row shows a month-year in the
 *      "Reconciled in Sage" column instead.
 *   4. otherwise 'issued'.
 *
 * The `sage_cleared` test is `=== 'T'` and NOT `!== 'F'` on purpose: the Sage
 * flag is TRI-state — `'T'` cleared, `'M'` matched, `'F'` unreconciled. `'M'`
 * means the check was auto-matched to a statement line but the reconciliation
 * has NOT been posted yet, so the match can still be broken; it is not proof of
 * payment. A `!== 'F'` test would also swallow NULL (never synced, e.g. rows
 * imported before DCP-320) and silently empty the Outstanding Checks list.
 * 'voided' only ever comes from an explicit override.
 *
 * Server-side twin: `checkStatusFilter` (`sage-check-status-filter.ts`) encodes
 * this same order as a PostgREST filter for the Status quick filter — change
 * both together.
 *
 * @param row - A sage_checks row (or compatible pick) carrying dcp_check_status,
 *              date_cashed and sage_cleared.
 * @returns The canonical DcpCheckStatus for display and calculation logic.
 */
export function effectiveCheckStatus(
  row: Pick<SageCheckRow, 'dcp_check_status' | 'date_cashed' | 'sage_cleared'>,
): DcpCheckStatus {
  if (row.dcp_check_status) return row.dcp_check_status as DcpCheckStatus
  if (row.date_cashed) return 'paid'
  // DCP-320: reconciled in Sage ⇒ the bank statement contained the check ⇒ paid.
  if (row.sage_cleared === 'T') return 'paid'
  return 'issued'
}

/**
 * Returns true when a check should count toward Outstanding Checks / Book Balance.
 * A check is outstanding only when its effective status is 'issued' AND it has
 * not been cashed.
 *
 * The `!date_cashed` clause is intentional: it keeps this client helper in exact
 * lockstep with the authoritative server-side definition of "effectively issued"
 * — the `outstandingOnly` filter in `sageChecks.list`/`count`, which is
 * `.is('date_cashed', null)` AND two flat disjunctions,
 * `.or('dcp_check_status.is.null,dcp_check_status.eq.issued')` AND the DCP-320
 * sage_cleared-aware `.or('dcp_check_status.eq.issued,sage_cleared.is.null,sage_cleared.neq.T')`
 * (the distributed form of the SQL predicate quoted below), and the FILTER
 * clauses in the aggregation RPCs. Without it, an `dcp_check_status='issued'`
 * override on a row that also carries a `date_cashed` would be counted here but
 * excluded server-side, diverging the SageIntacctPage badge from the list/tiles.
 *
 * LOCKSTEP CONTRACT (DCP-320). The authoritative SQL predicate, shared by
 * `sage_checks_outstanding_total`, `sage_checks_aggregate_by_entity` and
 * `get_cash_position_summary`, is:
 *
 *   c.state = 'C' AND c.date_cashed IS NULL
 *   AND ( c.dcp_check_status = 'issued'
 *      OR (c.dcp_check_status IS NULL AND COALESCE(c.sage_cleared, '') <> 'T') )
 *
 * This function is that predicate minus the `state` clause (callers gate on
 * `OUTSTANDING_CHECK_STATES` themselves). The two agree on every input:
 *   - override 'issued'          → SQL true (date null); here effective='issued' → true,
 *                                  INCLUDING when sage_cleared='T' — the human wins.
 *   - override 'paid'/'voided'   → SQL false (neither disjunct holds); here effective
 *                                  is not 'issued' → false.
 *   - no override + date_cashed  → SQL false (date_cashed IS NULL fails); here 'paid'.
 *   - no override + 'T'          → SQL false (COALESCE = 'T'); here 'paid' → false.
 *   - no override + 'M' / 'F'    → SQL true; here 'issued' → true.
 *   - no override + NULL         → SQL true (COALESCE → '' <> 'T'); here 'issued' → true.
 *     This is the un-synced case: rows that predate the DCP-320 sync must stay
 *     outstanding, never silently drop out of the list.
 *
 * @param row - A sage_checks row (or compatible pick) carrying dcp_check_status,
 *              date_cashed and sage_cleared.
 * @returns Whether the check is currently outstanding.
 */
export function isOutstandingCheck(
  row: Pick<SageCheckRow, 'dcp_check_status' | 'date_cashed' | 'sage_cleared'>,
): boolean {
  return effectiveCheckStatus(row) === 'issued' && !row.date_cashed
}

/**
 * Single source of truth for the cross-field rule when assigning a DCP status
 * override to a check (DCP-275). The row-level inline editor saves Status and
 * Date Cashed together, so the only invariant left to enforce is:
 *   - 'paid'   requires a Date Cashed (a cashed check must record WHEN it
 *              cleared, otherwise the Outstanding/Book Balance tiles can't
 *              attribute it to a period),
 *   - 'issued'/'voided' are always allowed — Date Cashed belongs to 'paid' only,
 *              so the editor/data layer clear the date on save for any non-paid
 *              status; an empty date is therefore never a *precondition* the
 *              caller has to satisfy beforehand.
 *
 * Shared by the data layer (`sageChecks.updateCheck`, which surfaces `reason`
 * as its error message after resolving the final date) and the inline UI editor
 * (`DateCashedCell`, which gates the Save button on the same rule), so the rule
 * cannot drift between them.
 *
 * @param status     - The status the user wants to assign.
 * @param dateCashed - The check's resolved Date Cashed (ISO date) or null.
 */
export function canAssignStatus(
  status: DcpCheckStatus,
  dateCashed: string | null,
): { ok: true } | { ok: false; reason: string } {
  if (status === 'paid' && dateCashed === null) {
    return { ok: false, reason: 'Status "Paid" requires a Date Cashed.' }
  }
  return { ok: true }
}

/**
 * Resolve the Date Cashed that should actually be persisted for a given status
 * (DCP-275). Date Cashed belongs to 'paid' ONLY — a cashed check records WHEN it
 * cleared; any other status ('issued'/'voided') clears the date so a stale value
 * can't survive and re-derive the row as 'paid' via `effectiveCheckStatus`.
 *
 * Single source of truth shared by the data layer (`sageChecks.updateCheck`), the
 * optimistic cache patch (`useUpdateSageCheck`), and the UI Save gate
 * (`OutstandingChecksPage`) so the "paid-only date" rule cannot drift between
 * them — it always pairs with `canAssignStatus`, which is validated against this
 * resolved date.
 *
 * @param status     - The status being assigned.
 * @param dateCashed - The Date Cashed the caller wants to set (ISO date) or null.
 * @returns The date to persist: the caller value for 'paid', otherwise null.
 */
export function resolveDateCashed(
  status: DcpCheckStatus,
  dateCashed: string | null,
): string | null {
  return status === 'paid' ? dateCashed : null
}

/**
 * Resolve the value that should actually be STORED in `dcp_check_status` when
 * the user saves a status (DCP-320).
 *
 * `dcp_check_status` is an override: whatever is stored there outranks Sage in
 * `effectiveCheckStatus` and in the SQL "effectively issued" predicate. Storing
 * 'issued' therefore pins the check open FOREVER — a later Sage reconciliation
 * (`sage_cleared` flipping to 'T') could never close it. So an Issued save only
 * persists the explicit 'issued' override when it genuinely CONTRADICTS Sage,
 * i.e. Sage currently says the check cleared (`'T'`) and the user disagrees.
 * In every other case ('F', 'M', NULL/never synced) Issued is already what the
 * row derives to, so NULL ("follow Sage") is stored instead — the row still
 * renders Issued today and can still be closed by Sage tomorrow.
 *
 * 'paid' and 'voided' are stored as-is: they are real human decisions that
 * must outrank Sage.
 *
 * Staleness: the decision uses the CLIENT's view of `sage_cleared`. If it is
 * stale (DB already 'T', client still 'F'), a deliberate Issued stores NULL and
 * the row shows Paid after the refetch — visible and self-correcting by editing
 * again, and consistent with "an override counts only when it contradicts what
 * Sage says".
 *
 * Single source of truth shared by the data layer (`sageChecks.updateCheck`)
 * and the optimistic cache patch (`useUpdateSageCheck`), so the optimistic row
 * always matches what the server persists.
 *
 * @param status      - The status the user saved.
 * @param sageCleared - The row's current `sage_cleared` flag ('T'/'F'/'M'/null).
 * @returns The `dcp_check_status` value to persist, or null to follow Sage.
 */
export function storedStatusFor(
  status: DcpCheckStatus,
  sageCleared: SageCheckRow['sage_cleared'],
): DcpCheckStatus | null {
  if (status !== 'issued') return status
  return sageCleared === 'T' ? 'issued' : null
}

/**
 * Per-row sync status for a facility, derived client-side from the latest
 * sync_state + sage_bank_accounts data. Drives the Status badge.
 */
export type FacilityStatus = 'synced' | 'unmatched' | 'pending' | 'not_configured'

/** Aggregated check counts and outstanding amount for one Sage bank account. */
export interface CheckAggregate {
  total: number
  outstanding: number
  outstandingAmount: number
}

/**
 * Compute the per-facility sync status from the three inputs that determine it.
 * The function is pure so it's easy to unit-test if we ever want to.
 *
 * @param sageLocationId - The facility's configured Sage location ID, or null if not set.
 * @param count - Number of Sage bank accounts matched to this location.
 * @param syncRanAtLeastOnce - Whether the bank-accounts sync has ever completed.
 * @returns One of four status strings driving the Status badge.
 *
 * @example
 * // Facility was never linked to Sage — admin still needs to fill the
 * // sage_location_id field on /settings/facilities.
 * deriveFacilityStatus({ sageLocationId: null, count: 0, syncRanAtLeastOnce: true })
 * // → 'not_configured'
 *
 * @example
 * // Configured + sync surfaced at least one matching bank account.
 * deriveFacilityStatus({ sageLocationId: 'PH_BF934', count: 3, syncRanAtLeastOnce: true })
 * // → 'synced'
 *
 * @example
 * // Configured but the bank-accounts sync hasn't run yet (e.g. brand-new
 * // integration). Show "Pending" instead of "Unmatched" so the user
 * // doesn't think the LOCATIONID is wrong before any data has flowed.
 * deriveFacilityStatus({ sageLocationId: 'PH_BF934', count: 0, syncRanAtLeastOnce: false })
 * // → 'pending'
 *
 * @example
 * // Configured AND sync has run, but Sage returned zero bank accounts for
 * // this LOCATIONID — typo in the facility row or the location really is
 * // empty in Sage. Surfaces as a red badge so the admin investigates.
 * deriveFacilityStatus({ sageLocationId: 'TYPO_42', count: 0, syncRanAtLeastOnce: true })
 * // → 'unmatched'
 */
export function deriveFacilityStatus({
  sageLocationId,
  count,
  syncRanAtLeastOnce,
}: {
  sageLocationId: string | null
  count: number
  syncRanAtLeastOnce: boolean
}): FacilityStatus {
  if (!sageLocationId) return 'not_configured'
  if (count > 0) return 'synced'
  // Has a sage_location_id but no matched bank accounts.
  if (!syncRanAtLeastOnce) return 'pending'
  return 'unmatched'
}

/**
 * Aggregate sage_checks rows by `financial_entity` so the Checks table can
 * render per-bank-account counts.
 *
 * Outstanding semantics: a check is outstanding when (a) its state is one
 * of `OUTSTANDING_CHECK_STATES` (Posted only, per DCP-244) AND (b) its effective
 * status (as determined by `isOutstandingCheck`) is 'issued'. The effective
 * status respects the DCP override column: a check with `dcp_check_status: 'voided'`
 * or `dcp_check_status: 'paid'` is excluded even when its Sage state is still 'C'
 * and `date_cashed` is null. Since DCP-320 it also respects Sage's reconciliation
 * flag — an un-overridden check with `sage_cleared: 'T'` is excluded, because the
 * bank statement already contained it. Routing every decision through
 * `isOutstandingCheck` (rather than re-deriving the rule here) is what keeps the
 * SageIntacctPage badge counter in lockstep with the list shown on
 * /banking/outstanding-checks and with the server-side SQL predicate quoted there.
 *
 * Bank-clearance tracking (Plaid debit cross-checking) is deferred to a
 * follow-up — once it lands, "outstanding" can become a derived signal
 * instead of an explicit override. Until then `isOutstandingCheck` is the
 * single authoritative definition of effective-issued status.
 *
 * @param checks - Array of sage_checks rows from the tenant database.
 * @returns Map keyed by financial_entity ID containing total, outstanding count, and outstanding amount.
 */
export function aggregateChecksByEntity(
  checks: SageCheckRow[],
): Map<string, CheckAggregate> {
  const out = new Map<string, CheckAggregate>()
  const outstandingSet: Set<string> = new Set(OUTSTANDING_CHECK_STATES)
  for (const c of checks) {
    if (!c.financial_entity) continue
    const entry = out.get(c.financial_entity) ?? {
      total: 0,
      outstanding: 0,
      outstandingAmount: 0,
    }
    entry.total += 1
    // Gate on both the Sage state allowlist AND the effective status:
    // `isOutstandingCheck` encapsulates the dcp_check_status override logic
    // (voided/paid overrides always win) and the date_cashed fallback.
    // Without the effective-status check the badge would over-count rows
    // the user has already voided via the DCP inline editor.
    if (c.state && outstandingSet.has(c.state) && isOutstandingCheck(c)) {
      entry.outstanding += 1
      // total_paid is the disbursed amount for Posted ('C') checks and is
      // the correct field to accumulate into the outstanding dollar total.
      // Generated types say total_paid is non-null
      // `number`, but we gate the null/undefined path because the
      // Supabase row shape can lie (column added before backfill, schema
      // drift). `?? 0` inside Number() avoids the
      // `Number(undefined) → NaN` trap that would propagate through the
      // running sum.
      //
      // The `Number.isFinite` filter guards against a second NaN-spread
      // path: if schema drift hands us a non-numeric value (`"abc"` from
      // a stringly-typed column, `null` after a future migration), the
      // outer `Number()` returns `NaN`, and a single `NaN +=` poisons
      // every subsequent sum for the entity — the SageIntacctPage badge
      // would render literal "NaN". Drop non-finite contributions on the
      // floor; the row is still counted via the `outstanding += 1` above
      // so the count stays honest.
      const amount = Number(c.total_paid ?? 0)
      if (Number.isFinite(amount)) {
        entry.outstandingAmount += amount
      } else if (import.meta.env.DEV) {
        // Operator-visible breadcrumb when an upstream column lies — the
        // outstanding count still increments, but the dollar total has
        // dropped this row. Without the warn, schema-drift bugs (e.g.
        // `total_paid` becoming a string column after a botched migration,
        // or a corrupt Sage payload landing as `'abc'`) would silently
        // under-report by an unknown amount.
        console.warn(
          `aggregateChecksByEntity: skipping non-finite total_paid for ` +
            `check record_no=${c.record_no}; raw value=${JSON.stringify(c.total_paid)}`,
        )
      }
    }
    out.set(c.financial_entity, entry)
  }
  return out
}

/**
 * Render a RenderedSageSyncWarning as a single human-readable line. For
 * `unmatched_location` we synthesize a sentence so the table doesn't have to
 * carry a `message`; other kinds use their `message` directly.
 *
 * @param warning - A validated warning from parseSageSyncWarnings().
 * @returns Human-readable string for display in the coverage warnings list.
 */
export function formatWarning(warning: RenderedSageSyncWarning): string {
  if (warning.kind === 'unmatched_location') {
    return `${warning.facility_name}: no bank accounts found for LOCATIONID ${warning.sage_location_id}`
  }
  return warning.message
}

/**
 * Build a Map keyed by sage_bank_accounts.location_id with the count of rows
 * for each location. Done client-side because the catalog is small (≤ low
 * hundreds) and we already have the array in memory.
 *
 * @param bankAccounts - Array of sage_bank_accounts rows.
 * @returns Map from location_id to the count of bank accounts for that location.
 */
export function countBankAccountsByLocation(
  bankAccounts: SageBankAccountRow[],
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const ba of bankAccounts) {
    counts.set(ba.location_id, (counts.get(ba.location_id) ?? 0) + 1)
  }
  return counts
}

/**
 * Build a Map keyed by sage_bank_accounts.location_id with the most recent
 * `last_seen_at` value across all bank accounts for that location. Used to
 * surface "last seen in Sage" per facility row.
 *
 * @param bankAccounts - Array of sage_bank_accounts rows.
 * @returns Map from location_id to the ISO timestamp string of the latest last_seen_at.
 */
export function lastSeenAtByLocation(
  bankAccounts: SageBankAccountRow[],
): Map<string, string> {
  const seen = new Map<string, string>()
  for (const ba of bankAccounts) {
    if (!ba.last_seen_at) continue
    const current = seen.get(ba.location_id)
    if (!current || ba.last_seen_at > current) {
      seen.set(ba.location_id, ba.last_seen_at)
    }
  }
  return seen
}

/**
 * Build a Map keyed by facilities.sage_location_id so the Checks coverage
 * table can label each Sage bank account with its matched facility name.
 *
 * **Duplicate handling:** `sage_location_id` is expected to be unique
 * across facilities (one DCP facility ↔ one Sage LOCATIONID). If an
 * admin accidentally assigns the same id to multiple facilities, the
 * last entry wins (Map.set overwrite), which would silently hide the
 * earlier facility from the coverage table. Dev runs surface that
 * configuration error via a `console.warn` so operators can fix the
 * data before it ships; production runs degrade silently to last-wins
 * because the alternative (throwing) would crash the whole settings
 * page over what's already a fixable data-integrity bug.
 *
 * @param facilities - Array of facility objects with name and sage_location_id.
 * @returns Map from sage_location_id to facility name, skipping facilities without a location ID.
 */
export function facilityNamesByLocation(
  facilities: { name: string; sage_location_id: string | null }[],
): Map<string, string> {
  const out = new Map<string, string>()
  for (const f of facilities) {
    if (!f.sage_location_id) continue

    const existing = out.get(f.sage_location_id)
    if (existing && import.meta.env.DEV) {
      console.warn(
        `facilityNamesByLocation: duplicate sage_location_id "${f.sage_location_id}" ` +
          `between facilities "${existing}" and "${f.name}". Using "${f.name}" — ` +
          `check facility settings for data integrity.`,
      )
    }

    out.set(f.sage_location_id, f.name)
  }
  return out
}
